import React, { useCallback, useState } from "react";
import { Form, Row, Col, DatePicker, message } from "antd";
import moment from "moment";
import { LoadStatus } from "@/utils/enum";
import { changeAdminActualDateRequest } from "@/services/loads";

export default function ActualForm({ shipmentData, formRef, setIsLoadingActualDelivery, refreshData }) {
  const [pickupWarning, setPickupWarning] = useState(null);
  const [dropoffWarning, setDropOffWarning] = useState(null);

  const onSubmit = useCallback(
    values => {
      const loadId = shipmentData?.load?.id;

      if (!loadId) return;

      setIsLoadingActualDelivery(true);

      const payload = {
        pickupAt: values?.pickup?.format("YYYY-MM-DDTHH:mm:ss.SSSZ") ?? null,
        dropOffAt: values?.dropoff?.format("YYYY-MM-DDTHH:mm:ss.SSSZ") ?? null,
      };

      changeAdminActualDateRequest(loadId, payload)
        .then(() => {
          message.success("Actual dates updated!");
          setIsLoadingActualDelivery(false);
          typeof refreshData === "function" && refreshData();
        })
        .catch(err => {
          console.log(err);
          message.error("Could not update Actual Dates");
          setIsLoadingActualDelivery(false);
        });
    },
    [shipmentData],
  );

  return (
    <Form
      layout="vertical"
      form={formRef}
      onFinish={onSubmit}
      initialValues={{
        pickup: shipmentData?.load?.pickedUpAt ? moment.utc(shipmentData?.load?.pickedUpAt).locale("en").local() : null,
        dropoff: shipmentData?.load?.droppedOffAt
          ? moment.utc(shipmentData?.load?.droppedOffAt).locale("en").local()
          : null,
      }}
    >
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            name="pickup"
            label="Actual Pickup"
            style={{ width: "100%" }}
            dependencies={["dropoff"]}
            validateStatus={pickupWarning ? "warning" : undefined}
            help={pickupWarning}
            rules={[
              {
                required: true,
                message: "Please select a pickup date",
              },
              {
                validator: async (_, value) => {
                  setPickupWarning(null);

                  const daysDiff = moment.utc(value).local().diff(moment(), "days");

                  if (daysDiff > 14) {
                    setPickupWarning(
                      "The Actual Pickup Date is more than 14 days from today. Please verify date is correct.",
                    );
                  }

                  return Promise.resolve(); // Always resolve so it's only a warning
                },
              },
              {
                warningOnly: true,
                validator: async (_, value) => {
                  setPickupWarning(null);

                  const dropOffDate = formRef?.current?.getFieldValue("dropoff");

                  const daysDiff = moment.utc(dropOffDate).local().diff(value, "days");

                  if (daysDiff > 7) {
                    setPickupWarning(
                      "The Actual Pickup Date is more than seven days before the Drop-off Date. Please verify these dates are accurate.",
                    );
                  }

                  return Promise.resolve(); // Always resolve so it's only a warning
                },
              },
            ]}
          >
            <DatePicker
              use12Hours
              format="MM/DD/YYYY h:mm A"
              showTime={{ format: "HH:mm" }}
              style={{ width: "100%" }}
              disabled={shipmentData?.load?.pickedUpAt == null && shipmentData?.load?.status < LoadStatus.PickedUp}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="dropoff"
            label="Actual Drop-off"
            style={{ width: "100%" }}
            dependencies={["pickup"]}
            validateStatus={dropoffWarning ? "warning" : undefined}
            help={dropoffWarning}
            rules={[
              {
                required: shipmentData?.load?.pickedUpAt == null,
                message: "Please select a dropoff date",
              },
              {
                warningOnly: true,
                validator: async (_, value) => {
                  setDropOffWarning(null);
                  const daysDiff = moment.utc(value).local().diff(moment(), "days");

                  if (daysDiff > 14) {
                    setDropOffWarning(
                      "The Actual Drop-off Date is more than 14 days from today. Please verify date is correct.",
                    );
                  }

                  return Promise.resolve(); // Always resolve so it's only a warning
                },
              },
              {
                warningOnly: true,
                validator: async (_, value) => {
                  setDropOffWarning(null);
                  const daysDiff = moment.utc(value).local().diff(moment(), "days");

                  if (daysDiff > 0) {
                    setDropOffWarning("The Actual Drop-off date is in the future. Is that date correct?");
                  }

                  return Promise.resolve(); // Always resolve so it's only a warning
                },
              },
            ]}
          >
            <DatePicker
              format="MM/DD/YYYY h:mm A"
              showTime={{ format: "HH:mm" }}
              use12Hours
              style={{ width: "100%" }}
              disabled={
                shipmentData?.load?.pickedUpAt == null ||
                shipmentData?.load?.droppedOffAt == null ||
                shipmentData?.load?.status < LoadStatus.Delivered
              }
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
